/**
 * Sanitize untrusted HTML intended to be displayed as an email preview.
 *
 * Threat model / goals:
 * - Treat input as untrusted (stored email body may be attacker-controlled).
 * - Prevent XSS: no scripts, no inline event handlers, no javascript: URLs, no unsafe SVG.
 * - Do not auto-load remote images by default: we drop all <img> tags.
 *
 * Approach:
 * - Parse with DOMParser
 * - Rebuild a safe DOM tree using an allowlist of tags + attributes
 * - Sanitize inline styles with a small allowlist of properties and value checks
 */
export function sanitizeEmailHtml(dirtyHtml: string): string {
  if (!dirtyHtml) return '';

  // DOMParser is available in browsers and in Jest's default jsdom environment.
  const parser = new DOMParser();
  const doc = parser.parseFromString(dirtyHtml, 'text/html');

  const outDoc = document.implementation.createHTMLDocument('sanitized-email');
  const outContainer = outDoc.createElement('div');

  // Conservative allowlist of common email tags.
  const ALLOWED_TAGS = new Set([
    'a',
    'b',
    'blockquote',
    'br',
    'code',
    'div',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'li',
    'ol',
    'p',
    'pre',
    'span',
    'strong',
    'table',
    'tbody',
    'td',
    'th',
    'thead',
    'tr',
    'u',
    'ul',
  ]);

  // Drop entirely (do not keep children) for active/content-loading elements.
  const DROP_TAGS = new Set([
    'script',
    'style',
    'iframe',
    'object',
    'embed',
    'link',
    'meta',
    'base',
    'svg',
    'math',
    'form',
    'input',
    'button',
    'textarea',
    'select',
    'option',
    'video',
    'audio',
    'source',
    'picture',
    // Images are blocked by default to avoid auto-loading remote resources.
    'img',
  ]);

  const GLOBAL_ATTRS = new Set(['title', 'aria-label', 'role']);
  const TABLE_ATTRS = new Set(['colspan', 'rowspan']);

  // Small set of style props commonly used in emails that are low-risk.
  const ALLOWED_STYLE_PROPS = new Set([
    'color',
    'background-color',
    'font-size',
    'font-family',
    'font-weight',
    'font-style',
    'text-decoration',
    'line-height',
    'text-align',
    'margin',
    'margin-top',
    'margin-right',
    'margin-bottom',
    'margin-left',
    'padding',
    'padding-top',
    'padding-right',
    'padding-bottom',
    'padding-left',
    'border',
    'border-top',
    'border-right',
    'border-bottom',
    'border-left',
    'border-color',
    'border-width',
    'border-style',
    'border-radius',
    'width',
    'max-width',
    'min-width',
  ]);

  const isSafeUrl = (raw: string) => {
    const v = (raw || '').trim().replace(/\s/g, '');
    if (!v) return false;
    // Allow in-page anchors and relative URLs
    if (v.startsWith('#') || v.startsWith('/')) return true;
    try {
      const u = new URL(v, window.location.origin);
      return ['http:', 'https:', 'mailto:', 'tel:'].includes(u.protocol);
    } catch {
      return false;
    }
  };

  const sanitizeStyle = (styleText: string) => {
    const parts = (styleText || '')
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean);

    const safe: string[] = [];
    for (const part of parts) {
      const idx = part.indexOf(':');
      if (idx <= 0) continue;
      const prop = part.slice(0, idx).trim().toLowerCase();
      const value = part.slice(idx + 1).trim();
      if (!ALLOWED_STYLE_PROPS.has(prop)) continue;

      const lowerVal = value.toLowerCase();
      // Block common CSS vectors / remote loads.
      if (
        lowerVal.includes('url(') ||
        lowerVal.includes('expression(') ||
        lowerVal.includes('@import')
      ) {
        continue;
      }
      safe.push(`${prop}: ${value}`);
    }
    return safe.join('; ');
  };

  const sanitizeNode = (node: Node, outParent: HTMLElement) => {
    // Text nodes: safe as-is.
    if (node.nodeType === Node.TEXT_NODE) {
      outParent.appendChild(outDoc.createTextNode(node.textContent || ''));
      return;
    }

    // Comments, etc: drop.
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (DROP_TAGS.has(tag)) return;

    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap unknown tags (keep children) to preserve readable content.
      Array.from(el.childNodes).forEach((child) => sanitizeNode(child, outParent));
      return;
    }

    const outEl = outDoc.createElement(tag);

    // Attributes: allow only a tight set. (We do not preserve class/id.)
    for (const { name, value } of Array.from(el.attributes)) {
      const attr = name.toLowerCase();
      if (attr.startsWith('on')) continue; // inline handlers

      if (attr === 'style') {
        const safeStyle = sanitizeStyle(value);
        if (safeStyle) outEl.setAttribute('style', safeStyle);
        continue;
      }

      if (GLOBAL_ATTRS.has(attr)) {
        outEl.setAttribute(attr, value);
        continue;
      }

      if ((tag === 'td' || tag === 'th') && TABLE_ATTRS.has(attr)) {
        outEl.setAttribute(attr, value);
        continue;
      }

      if (tag === 'a' && attr === 'href') {
        if (isSafeUrl(value)) outEl.setAttribute('href', value);
        continue;
      }

      // Everything else is dropped.
    }

    // Enforce safe link behaviors.
    if (tag === 'a') {
      outEl.setAttribute('target', '_blank');
      outEl.setAttribute('rel', 'noopener noreferrer');
    }

    Array.from(el.childNodes).forEach((child) => sanitizeNode(child, outEl));
    outParent.appendChild(outEl);
  };

  Array.from(doc.body.childNodes).forEach((n) => sanitizeNode(n, outContainer));
  return outContainer.innerHTML;
}


